import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, ChevronUp, HelpCircle } from "lucide-react";

const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const questions = [
    {
      question: "What is EasyMarg?",
      answer: "EasyMarg is a digital platform designed to simplify the management of financial and academic tasks. We connect professionals and learners through various services.",
    },
    {
      question: "How can I book a consultation?",
      answer: "You can book a consultation through our platform by selecting a professional or expert and picking a time slot for your session.",
    },
    {
      question: "How does the payment process work?",
      answer: "Payments are processed through secure gateways like Razorpay. After confirming the service details, you can make the payment securely and track your transactions.",
    },
    {
      question: "Can I return a purchased product?",
      answer: "Once a product is purchased, we offer a return policy depending on the product type. Please check the product details for more information on returns and refunds.",
    },
  ];

  const handleToggle = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] font-outfit relative pb-20">

      {/* Dark Hero Background for Navbar Visibility */}
      <div className="absolute top-0 left-0 w-full h-[180px] bg-[#020617] z-0 overflow-hidden rounded-b-[2rem] shadow-xl">
        <div className="absolute inset-0 opacity-20">
          <img src="https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&q=80&w=2000" alt="cover" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/50 to-[#020617]"></div>
      </div>

      {/* Container */}
      <div className="relative z-10 pt-24 max-w-7xl w-full mx-auto px-4 md:px-6">

        {/* Breadcrumbs */}
        <div className="text-white text-xs font-bold uppercase tracking-widest mb-6 opacity-80 flex items-center gap-2">
          <a href="/home" className="hover:text-blue-300 transition-colors">Home</a>
          <span>/</span>
          <span>FAQ</span>
        </div>

        {/* White Canvas */}
        <div className="bg-white rounded-[2rem] shadow-xl border border-slate-100 p-6 md:p-10 overflow-hidden">

          {/* Compact Header — same style as Contact page */}
          <div className="flex items-start gap-4 mb-8 pb-6 border-b border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
              <HelpCircle className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">Frequently Asked Questions</h2>
              <p className="text-xs text-slate-500 mt-0.5">Find answers to common questions about EasyMarg and our services.</p>
            </div>
          </div>

          {/* FAQ Accordion */}
          <div className="space-y-3">
            {questions.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05, duration: 0.3 }}
                viewport={{ once: true }}
                className="rounded-2xl overflow-hidden border border-slate-200"
              >
                <button
                  onClick={() => handleToggle(index)}
                  className={`w-full px-5 py-4 flex justify-between items-center text-left transition-colors ${
                    activeIndex === index ? "bg-slate-50 text-blue-700" : "bg-white text-slate-800 hover:bg-slate-50"
                  }`}
                >
                  <span className="text-sm font-semibold">{faq.question}</span>
                  {activeIndex === index ? (
                    <ChevronUp size={18} className="text-blue-600 shrink-0 ml-4" />
                  ) : (
                    <ChevronDown size={18} className="text-slate-400 shrink-0 ml-4" />
                  )}
                </button>
                <AnimatePresence>
                  {activeIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                    >
                      <div className="px-5 pb-5 pt-2 text-xs text-slate-600 leading-relaxed bg-slate-50 border-t border-slate-100">
                        {faq.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
};

export default FAQ;
